/**
 * The clock of a timed course (course version 2). The run starts on the tick the feet cross the
 * start line moving right, at the fraction of the tick where the crossing happened, so a time is
 * resolved finer than a tick and a tape reproduces it exactly. Going back behind the start line
 * cancels the run: the warm-up area before it is free play. A fall into water keeps the clock
 * running and counts; a restart puts the body back in the warm-up area. Allocation-free.
 */

import type { EventBuffer } from "./events";
import type { SimState } from "./state";
import { Ev, HZ, Run, RunCancel } from "./types";
import type { World } from "./world";

/** Fraction of the tick at which a motion from `x0` to `x1` crossed `line` (x0 < line <= x1). */
function crossing(x0: number, x1: number, line: number): number {
  const d = x1 - x0;
  return d > 0 ? (line - x0) / d : 1;
}

/**
 * Advance the run after the body has moved this tick. `prevX` is the feet x of the previous tick,
 * `respawnBefore` the respawn index before this tick's progress tracking, and `teleported` is true
 * on a tick that respawned the body (a restart or the end of a fall), whose motion is not a crossing.
 */
export function trackRun(
  world: World,
  s: SimState,
  ev: EventBuffer,
  prevX: number,
  respawnBefore: number,
  teleported: boolean,
  restarted: boolean,
): void {
  if (!world.timed) return;
  const start = world.startLineX;
  if (restarted && s.run === Run.Finished) s.run = Run.Ready;
  if (s.run === Run.Running && s.x < start) {
    s.run = Run.Ready;
    // Checkpoints passed in the abandoned run do not carry into the next one.
    s.respawnIndex = 0;
    const reason = restarted
      ? RunCancel.Restart
      : teleported
        ? RunCancel.RespawnedBehindStart
        : RunCancel.BehindStart;
    ev.push(Ev.RunCancelled, s.tick, reason);
  }
  if (teleported) return;
  if (s.run === Run.Ready && prevX < start && s.x >= start) {
    s.run = Run.Running;
    s.runStart = s.tick - 1 + crossing(prevX, s.x, start);
    s.runTime = 0;
    s.runFalls = 0;
    ev.push(Ev.RunStarted, s.tick, s.runStart);
  }
  if (s.run !== Run.Running) return;
  if (s.respawnIndex > respawnBefore) {
    const at = world.respawnX[s.respawnIndex]!;
    const split = (s.tick - 1 + crossing(prevX, s.x, at) - s.runStart) / HZ;
    ev.push(Ev.Checkpoint, s.tick, s.respawnIndex, split);
  }
  const finish = world.finishLineX;
  if (prevX < finish && s.x >= finish) {
    s.run = Run.Finished;
    s.runTime = (s.tick - 1 + crossing(prevX, s.x, finish) - s.runStart) / HZ;
    ev.push(Ev.RunFinished, s.tick, s.runTime, s.runFalls);
  }
}
